// POST {apps_origin}/apps/_session — the one call that makes widget documents
// loadable.
//
// A widget document is served from the apps host, which has no access to the
// product's Auth0 session: it authenticates with a `__Host-apps_sess` cookie and
// nothing else. Only the product holds a bearer that can mint that cookie, so
// this exchange has to happen up here, cross-origin with `credentials: 'include'`
// so the Set-Cookie lands on the apps origin, and it has to finish BEFORE the
// first iframe is created — a frame that beats the cookie gets the 401 page.
//
// Every non-204 outcome means the same thing to a page: no widgets. It is a
// state, not an exception, so it comes back as a value and is logged here rather
// than thrown at each caller.

import { getToken } from '@luminovo/auth';

export const APPS_SESSION_PATH = '/apps/_session';

export type AppsSessionFailure =
    // 403 wrong_product_origin — the apps host does not recognise this page's
    // origin as its paired product. In practice: the page is not on the tenant's
    // canonical .com host (a preview alias, a vanity domain, a raw cluster
    // hostname), so the pairing can never succeed from here.
    | 'wrong_product_origin'
    // 401/403 without that code — the bearer was rejected.
    | 'unauthorized'
    // Any other status. The apps host answered, but not with a session.
    | 'rejected'
    // Never answered: offline, DNS, TLS, or a CORS preflight that failed. The
    // browser hides which, by design — which makes this, not the branch above,
    // where a wrong product origin usually lands. The apps host answers the
    // preflight with 403 and NO CORS headers, so the browser refuses the request
    // and the real 403 never reaches this code.
    | 'unreachable'
    // Took longer than APPS_SESSION_TIMEOUT_MS. Distinct from 'unreachable'
    // because the remedy differs and because a hang is the one failure a caller
    // cannot wait out: with no deadline the whole slot surface sits on its loading
    // fallback until the browser abandons the fetch, minutes later.
    | 'timed_out';

export type AppsSessionResult =
    | { status: 'ready' }
    | { status: 'unavailable'; failure: AppsSessionFailure; httpStatus?: number };

// Matches SlotContainer's default readyTimeoutMs: both answer "how long may a
// widget take before we call it broken", and splitting them would let one path
// wait while the other has already given up.
export const APPS_SESSION_TIMEOUT_MS = 10_000;

export interface CreateAppsSessionOptions {
    signal?: AbortSignal;
    fetchFn?: typeof fetch;
    timeoutMs?: number;
}

// The 4xx bodies carry `{ error: '<code>' }`. Best-effort: an unreadable or
// non-JSON body just means we cannot tell one 403 from another.
const readErrorCode = async (response: Response): Promise<string | undefined> => {
    try {
        const body: unknown = await response.json();
        const code = (body as { error?: unknown } | null)?.error;
        return typeof code === 'string' ? code : undefined;
    } catch {
        return undefined;
    }
};

const classify = (status: number, errorCode: string | undefined): AppsSessionFailure => {
    if (status === 403 && errorCode === 'wrong_product_origin') {
        return 'wrong_product_origin';
    }
    return status === 401 || status === 403 ? 'unauthorized' : 'rejected';
};

const logUnavailable = (appsOrigin: string, failure: AppsSessionFailure, status?: number): void => {
    if (failure === 'wrong_product_origin') {
        console.error(
            `[apps] ${appsOrigin} refused to pair with ${window.location.origin}: this page is not on the tenant's canonical product host, so no widgets can load here.`,
        );
        return;
    }
    const hint =
        failure === 'unreachable'
            ? ` The likeliest cause is that ${window.location.origin} is not the product origin ${appsOrigin} pairs with: it refuses that preflight without CORS headers, so the 403 is invisible here.`
            : failure === 'timed_out'
              ? ` It did not answer within ${APPS_SESSION_TIMEOUT_MS}ms; the request was abandoned rather than left to hang.`
              : '';
    console.warn(
        `[apps] no apps session (${failure}${status === undefined ? '' : ` ${status}`}) from ${appsOrigin} — rendering no widgets on this page.${hint}`,
    );
};

export async function createAppsSession(
    appsOrigin: string,
    { signal, fetchFn = fetch, timeoutMs = APPS_SESSION_TIMEOUT_MS }: CreateAppsSessionOptions = {},
): Promise<AppsSessionResult> {
    // Outside the try: a missing product token is a bootstrap bug in the caller,
    // not a session state, and must not be reported as an unreachable apps host.
    const token = getToken();

    // Our own deadline, chained to the caller's signal rather than replacing it, so
    // a react-query cancellation still aborts the request and still reads as a
    // cancellation below. Built by hand instead of with AbortSignal.any(), which is
    // newer than the browsers this frontend supports.
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeoutMs);
    const abortFromCaller = () => controller.abort();
    signal?.addEventListener('abort', abortFromCaller);

    let response: Response;
    try {
        response = await fetchFn(`${appsOrigin}${APPS_SESSION_PATH}`, {
            method: 'POST',
            credentials: 'include',
            signal: controller.signal,
            headers: { Authorization: `Bearer ${token}` },
        });
    } catch (error) {
        // A cancelled request is not a verdict about the apps host — rethrow so
        // the caller discards it instead of caching "unavailable". Checked against
        // the CALLER's signal, so our own deadline falls through to a verdict.
        if (signal?.aborted) {
            throw error;
        }
        const failure = controller.signal.aborted ? 'timed_out' : 'unreachable';
        logUnavailable(appsOrigin, failure);
        return { status: 'unavailable', failure };
    } finally {
        window.clearTimeout(timer);
        signal?.removeEventListener('abort', abortFromCaller);
    }

    // 204 and only 204. A 200 with a body would mean the apps host answered with
    // something other than the contract we wrote against.
    if (response.status === 204) {
        return { status: 'ready' };
    }

    const failure = classify(response.status, await readErrorCode(response));
    logUnavailable(appsOrigin, failure, response.status);
    return { status: 'unavailable', failure, httpStatus: response.status };
}
